import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface NetworkStatus {
  level: number; // 0,1,2,3
  icon: string; // material icon name
  color: string; // material color (primary/accent/warn)
  label: string; // status text
}

@Injectable({
  providedIn: 'root',
})
export class NetworkService {
  private networkSubject = new BehaviorSubject<NetworkStatus>({
    level: 3,
    icon: 'signal_wifi_4_bar',
    color: 'primary',
    label: 'Strong',
  });

  network$ = this.networkSubject.asObservable();

  constructor() {
    this.initializeListeners();
    this.updateNetworkStatus();
  }

  private initializeListeners() {
    // Browser online/offline events
    window.addEventListener('online', () => this.updateNetworkStatus());
    window.addEventListener('offline', () => this.updateNetworkStatus());

    // Network Information API (Chrome / Edge / Android)
    const conn = (navigator as any)?.connection;
    if (conn) {
      conn.addEventListener('change', () => this.updateNetworkStatus());
    }
  }

  private updateNetworkStatus() {
    // If browser is offline
    if (!navigator.onLine) {
      this.networkSubject.next({
        level: 0,
        icon: 'android_cell_4_bar_off',
        color: 'warn',
        label: 'No Connection',
      });
      return;
    }

    // Get Network Information API details
    const conn = (navigator as any)?.connection;
    const downlink = conn?.downlink ?? 5; // Default strong if unsupported
    let status: NetworkStatus;
    if (downlink <= 0.2) {
      status = {
        level: 0,
        icon: 'android_cell_4_bar_off',
        color: 'warn',
        label: 'No Connection',
      };
    } else if (downlink <= 1) {
      status = {
        level: 1,
        icon: 'signal_cellular_alt_1_bar',
        color: 'warn',
        label: 'Very Weak Network',
      };
    } else if (downlink <= 3) {
      status = {
        level: 2,
        icon: 'signal_cellular_alt_2_bar',
        color: 'accent',
        label: 'Medium Network',
      };
    } else {
      status = {
        level: 3,
        icon: 'signal_cellular_alt',
        color: 'primary',
        label: 'Strong Network',
      };
    }

    this.networkSubject.next(status);
  }
}
